import React, { useState, useEffect, useCallback } from 'react';
import {
    Loader2, Save, XCircle, User, Mail, Phone, Calendar,
    Briefcase, Tag, CheckSquare, CreditCard, AlertTriangle, Upload, Info, GitBranch, Building2, Check, Lock, Unlock, Clock, RefreshCw
} from 'lucide-react';
import CustomDatePicker from './common/CustomDatePicker';
import { apiClient, BASE_URL } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { turkeyCities } from '../utils/turkeyCities';
import EmploymentHistoryModal from './EmploymentHistoryModal';

const BLOOD_TYPE_MAP = {
    'A+': 'A Rh+', 'A RH+': 'A Rh+', 'A Rh+': 'A Rh+',
    'A-': 'A Rh-', 'A RH-': 'A Rh-', 'A Rh-': 'A Rh-',
    'B+': 'B Rh+', 'B RH+': 'B Rh+', 'B Rh+': 'B Rh+',
    'B-': 'B Rh-', 'B RH-': 'B Rh-', 'B Rh-': 'B Rh-',
    'AB+': 'AB Rh+', 'AB RH+': 'AB Rh+', 'AB Rh+': 'AB Rh+',
    'AB-': 'AB Rh-', 'AB RH-': 'AB Rh-', 'AB Rh-': 'AB Rh-',
    '0+': '0 Rh+', 'O+': '0 Rh+', '0 RH+': '0 Rh+', 'O RH+': '0 Rh+', '0 Rh+': '0 Rh+',
    '0-': '0 Rh-', 'O-': '0 Rh-', '0 RH-': '0 Rh-', 'O RH-': '0 Rh-', '0 Rh-': '0 Rh-'
};

const normalizeBloodType = (val) => {
    if (!val) return '';
    const trimmed = String(val).trim();
    return BLOOD_TYPE_MAP[trimmed] || BLOOD_TYPE_MAP[trimmed.toUpperCase()] || trimmed;
};

const normalizeUserRole = (rawRole, availableRoles = []) => {
    if (!rawRole || rawRole === 'Rol Atanmamış') return 'Personel';
    const firstRole = String(rawRole).split(',')[0].trim();
    if (!firstRole || firstRole === 'Rol Atanmamış') return 'Personel';
    if (Array.isArray(availableRoles) && availableRoles.length > 0) {
        const matched = availableRoles.find(r => r.name === firstRole);
        if (matched) return matched.name;
    }
    return firstRole;
};

const scopeStringToValue = {
    DepartmentAndLocation: 5,
    Location: 4,
    AllData: 3,
    Department: 2,
    OwnData: 1,
    None: 0,
};

// string veya sayı gelse de 0..5 arası sayıya çevir
const normalizeScopeValue = (v) => {
    if (typeof v === 'number') return (v >= 0 && v <= 5) ? v : 1;
    if (typeof v === 'string') return scopeStringToValue[v] ?? 1;
    return 1;
};

// YENİ: Tarih formatlama (Timezone hatasını önlemek için string split kullanıyoruz)
const formatDateForInput = (dateVal) => {
    if (!dateVal) return '';
    // Eğer string ise ve T içeriyorsa direkt böl, yoksa olduğu gibi döndür (YYYY-MM-DD varsayımı)
    if (typeof dateVal === 'string') {
        if (dateVal.includes('T')) return dateVal.split('T')[0];
        return dateVal;
    }
    // Date objesi ise local string yap
    if (dateVal instanceof Date) {
        const y = dateVal.getFullYear();
        const m = String(dateVal.getMonth() + 1).padStart(2, '0');
        const d = String(dateVal.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }
    return '';
};

export default function PersonnelForm({
    personnel,
    onSave,
    onCancel,
    isNew,
    submitting: propSubmitting,
    roles = []
}) {
    const { firmCode, permissions } = useAuth();

    const [formData, setFormData] = useState(
        personnel ? {
            id: personnel.id,
            firstName: personnel.firstName || '',
            lastName: personnel.lastName || '',
            tcNo: personnel.turkishIdNo || '',
            sicilNo: personnel.sicilNo || '',
            email: personnel.email || '',
            phoneNumber: personnel.phoneNumber || '',
            departmentId: personnel.departmentId || '',
            positionId: personnel.positionId || '',
            locationId: personnel.locationId || '', 
            workingRegimeId: personnel.workingRegimeId || '', 
            hireDate: formatDateForInput(personnel.hireDate),
            dateOfBirth: formatDateForInput(personnel.dateOfBirth),
            terminationDate: formatDateForInput(personnel.terminationDate),

            terminationReason: personnel.terminationReason || '',

            seniorityDate: formatDateForInput(personnel.seniorityDate),

            previousLiquidatedYears: personnel.previousLiquidatedYears || '',

            isActive: personnel.isActive ?? true,
            userRole: normalizeUserRole(personnel.userRole, roles),
            gender: personnel.details?.gender || 'Male',
            bloodType: normalizeBloodType(personnel.details?.bloodType),
            cityId: personnel.details?.cityId || '',
            districtName: personnel.details?.districtName || '',
            neighborhood: personnel.details?.neighborhood || '',
            addressLine: personnel.details?.addressLine || '',
            postalCode: personnel.details?.postalCode || '',
            emergencyContactName: personnel.details?.emergencyContactName || '',
            emergencyContactPhone: personnel.details?.emergencyContactPhone || '',
            notes: personnel.details?.notes || ''
        } : {
            firstName: '', lastName: '', tcNo: '', sicilNo: '', email: '', phoneNumber: '',
            departmentId: '', positionId: '', locationId: '', workingRegimeId: '',
            hireDate: '', dateOfBirth: '', terminationDate: '',
            isActive: true, userRole: 'Personel',
            gender: 'Male', bloodType: '', cityId: '', districtName: '', neighborhood: '',
            addressLine: '', postalCode: '', emergencyContactName: '', emergencyContactPhone: '', notes: ''
        }
    );

    const [formError, setFormError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [isSicilUnlocked, setIsSicilUnlocked] = useState(false);
    const [syncWorkScheduleFromRegime, setSyncWorkScheduleFromRegime] = useState(true);
    const [regimeEffectiveDate, setRegimeEffectiveDate] = useState(() => formatDateForInput(new Date()));

    const currentSubmittingState = propSubmitting || submitting;

    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

    const [showAdvancedSeniority, setShowAdvancedSeniority] = useState(
        !!(personnel?.seniorityDate || personnel?.previousLiquidatedYears)
    );

    const [departments, setDepartments] = useState([]);
    const [positions, setPositions] = useState([]);
    const [locations, setLocations] = useState([]); // YENI
    const [workingRegimes, setWorkingRegimes] = useState([]); // YENI
    const [fetchingDropdowns, setFetchingDropdowns] = useState(true);
    const [deptError, setDeptError] = useState(null);
    const [posError, setPosError] = useState(null);

    // Holding / Alt Firma Yetkileri States
    const [subCompanies, setSubCompanies] = useState([]);
    const [selectedSubCompanies, setSelectedSubCompanies] = useState([]);
    const [loadingSubCompanies, setLoadingSubCompanies] = useState(false);

    const canViewSubCompanies = permissions?.includes('sub_companies:view');

    useEffect(() => {
        if (canViewSubCompanies) {
            const loadSubCompanies = async () => {
                setLoadingSubCompanies(true);
                try {
                    const allSubs = await apiClient('Holding/all-sub-companies');
                    setSubCompanies(allSubs || []);
                    
                    if (personnel && personnel.userId) {
                        const userAccess = await apiClient(`Holding/user-access/${personnel.userId}`);
                        setSelectedSubCompanies(userAccess || []);
                    }
                } catch (e) {
                    console.error("Alt firmalar yüklenirken hata oluştu:", e);
                } finally {
                    setLoadingSubCompanies(false);
                }
            };
            loadSubCompanies();
        }
    }, [canViewSubCompanies, personnel]);

    const handleToggleSubCompany = (id) => {
        setSelectedSubCompanies(prev => 
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    // Varsayılan veri kapsamı: OwnData
    const [dataScope, setDataScope] = useState({ dataAccessType: 1, departmentIds: [], locationIds: [] });

    // YENİ: Fotoğraf Upload State'leri
    const currentFirmCode = localStorage.getItem('firmCode') || '';
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(
        personnel?.id && personnel?.hasProfileImage ? `${BASE_URL}/EmployeeProfileMedia/${personnel.id}/download?firmCode=${currentFirmCode}&t=${Date.now()}` : null
    );
    const fileInputRef = React.useRef(null);

    // --- Dropdown fetchers ---
    const fetchDepartments = useCallback(async () => {
        setDeptError(null);
        try {
            const data = await apiClient('Department');
            setDepartments(data);
        } catch (err) {
            console.error('Bölümler çekilirken hata oluştu:', err);
            setDeptError('Bölümleri getirirken bir hata oluştu.');
        }
    }, []);

    const fetchPositions = useCallback(async () => {
        setPosError(null);
        try {
            const data = await apiClient('Position');
            setPositions(data);
        } catch (err) {
            console.error('Pozisyonlar çekilirken hata oluştu:', err);
            setPosError('Pozisyonları getirirken bir hata oluştu.');
        }
    }, []);

    // --- Form initial fetch ---
    useEffect(() => {
        const fetchFormData = async () => {
            setFetchingDropdowns(true);
            setFormError(null);
            try {
                const deptPromise = apiClient('Department');
                const posPromise = apiClient('Position');
                const locPromise = apiClient('Location'); // YENI
                const wrPromise = apiClient('WorkingRegime'); // YENI

                let initialFormData = {
                    firstName: '', lastName: '', tcNo: '', sicilNo: '', email: '', phoneNumber: '',
                    departmentId: '', positionId: '', locationId: '', workingRegimeId: '',
                    hireDate: '', dateOfBirth: '',
                    terminationDate: '', terminationReason: '', seniorityDate: '', previousLiquidatedYears: '', isActive: true, userRole: 'Personel',
                    gender: 'Male', bloodType: '', cityId: '', districtName: '', neighborhood: '',
                    addressLine: '', postalCode: '', emergencyContactName: '', emergencyContactPhone: '', notes: ''
                };
                let initialDataScope = { dataAccessType: 1, departmentIds: [], locationIds: [] };

                if (personnel && personnel.id) {
                    // datascope 404 olursa varsayılan değere düş
                    const scopePromise = (async () => {
                        try { return await apiClient(`Employee/${personnel.id}/datascope`); }
                        catch { return { dataAccessType: 'OwnData', departmentIds: [], locationIds: [] }; }
                    })();

                    const [deptData, posData, locData, wrData, scopeData] = await Promise.all([deptPromise, posPromise, locPromise, wrPromise, scopePromise]);
                    setDepartments(deptData || []);
                    setPositions(posData || []);
                    setLocations(locData || []);
                    setWorkingRegimes(wrData || []);

                    initialDataScope = {
                        dataAccessType: normalizeScopeValue(scopeData.dataAccessType),
                        departmentIds: scopeData.departmentIds || [],
                        locationIds: scopeData.locationIds || []
                    };

                    initialFormData = {
                        id: personnel.id,
                        firstName: personnel.firstName || '',
                        lastName: personnel.lastName || '',
                        tcNo: personnel.turkishIdNo || '',
                        sicilNo: personnel.sicilNo || '',
                        email: personnel.email || '',
                        phoneNumber: personnel.phoneNumber || '',
                        departmentId: personnel.departmentId || '',
                        positionId: personnel.positionId || '',
                        locationId: personnel.locationId || '', 
                        workingRegimeId: personnel.workingRegimeId || '', 
                        hireDate: formatDateForInput(personnel.hireDate),
                        dateOfBirth: formatDateForInput(personnel.dateOfBirth),
                        terminationDate: formatDateForInput(personnel.terminationDate),

                        terminationReason: personnel.terminationReason || '',

                        seniorityDate: formatDateForInput(personnel.seniorityDate),

                        previousLiquidatedYears: personnel.previousLiquidatedYears || '',

                        isActive: personnel.isActive ?? true,
                        userRole: personnel.userRole || 'Personel',
                        gender: personnel.details?.gender || 'Male',
                        bloodType: normalizeBloodType(personnel.details?.bloodType),
                        cityId: personnel.details?.cityId || '',
                        districtName: personnel.details?.districtName || '',
                        neighborhood: personnel.details?.neighborhood || '',
                        addressLine: personnel.details?.addressLine || '',
                        postalCode: personnel.details?.postalCode || '',
                        emergencyContactName: personnel.details?.emergencyContactName || '',
                        emergencyContactPhone: personnel.details?.emergencyContactPhone || '',
                        notes: personnel.details?.notes || ''
                    };
                } else {
                    const [deptData, posData, locData, wrData] = await Promise.all([deptPromise, posPromise, locPromise, wrPromise]);
                    setDepartments(deptData);
                    setPositions(posData);
                    setLocations(locData);
                    setWorkingRegimes(wrData);
                }

                setFormData(initialFormData);
                setDataScope(initialDataScope);

                // Eğer personelin hiç aktif vardiyası yoksa varsayılan geçerlilik tarihi işe giriş tarihi olsun, varsa bugün olsun
                const defaultEffDate = (personnel && !personnel.currentWorkPeriodId && initialFormData.hireDate)
                    ? initialFormData.hireDate
                    : formatDateForInput(new Date());
                setRegimeEffectiveDate(defaultEffDate);
                setSyncWorkScheduleFromRegime(true);

                if (personnel && (personnel.seniorityDate || personnel.previousLiquidatedYears)) {

                    setShowAdvancedSeniority(true);

                }
            } catch (err) {
                console.error('Form verileri çekilirken hata:', err);
                setFormError('Gerekli veriler yüklenirken bir hata oluştu: ' + err.message);
            } finally {
                setFetchingDropdowns(false);
            }
        };

        fetchFormData();
    }, [personnel]);

    // --- helpers ---
    const formatPhoneNumberDisplay = (num) => {
        if (!num) return '';
        let clean = String(num).replace(/\D/g, '');
        // 0 veya 90 ile başlıyorsa temizle (state'de temiz tutmaya çalışıyoruz ama yine de)
        if (clean.startsWith('0')) clean = clean.substring(1);
        if (clean.startsWith('90')) clean = clean.substring(2);

        if (clean.length === 0) return '';

        // Kademeli formatlama: 0 (5XX) XXX XX XX
        if (clean.length < 4) return `0 (${clean}`;
        if (clean.length < 7) return `0 (${clean.slice(0, 3)}) ${clean.slice(3)}`;
        if (clean.length < 9) return `0 (${clean.slice(0, 3)}) ${clean.slice(3, 6)} ${clean.slice(6)}`;
        return `0 (${clean.slice(0, 3)}) ${clean.slice(3, 6)} ${clean.slice(6, 8)} ${clean.slice(8, 10)}`;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'tcNo') {
            const v = value.replace(/\D/g, '');
            if (v.length <= 11) setFormData(p => ({ ...p, [name]: v }));
        } else if (name === 'phoneNumber') {
            // Sadece rakamları al
            let v = value.replace(/\D/g, '');

            // Kullanıcı 0 veya 90 ile başlarsa temizle (Standardize: 5XX...)
            if (v.startsWith('0')) v = v.substring(1);
            if (v.startsWith('90')) v = v.substring(2);

            // Max 10 hane ile sınırla
            if (v.length > 10) v = v.slice(0, 10);

            setFormData(p => ({ ...p, [name]: v }));
        } else {
            setFormData(p => ({ ...p, [name]: (type === 'checkbox' ? checked : value) }));
        }
    };

    const handleDateChange = (dateString, name) => {
        setFormData(prev => {
            const next = { ...prev, [name]: dateString };
            if (name === 'terminationDate') {
                if (dateString) {
                    const todayStr = formatDateForInput(new Date());
                    if (dateString <= todayStr) {
                        next.isActive = false;
                    }
                } else {
                    next.isActive = true;
                }
            }
            return next;
        });
    };

    const handleDataScopeTypeChange = (e) => {
        const type = Number(e.target.value);
        setDataScope(prev => ({
            ...prev,
            dataAccessType: type,
            departmentIds: (type === 2 || type === 5) ? prev.departmentIds : [],
            locationIds: (type === 4 || type === 5) ? prev.locationIds : []
        }));
    };

    const handleDepartmentCheckboxChange = (departmentId) => {
        setDataScope(prev => {
            const s = new Set(prev.departmentIds);
            s.has(departmentId) ? s.delete(departmentId) : s.add(departmentId);
            return { ...prev, departmentIds: Array.from(s) };
        });
    };

    const handleLocationCheckboxChange = (locationId) => {
        setDataScope(prev => {
            const s = new Set(prev.locationIds);
            s.has(locationId) ? s.delete(locationId) : s.add(locationId);
            return { ...prev, locationIds: Array.from(s) };
        });
    };

    // --- submit ---
    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setFormError("Dosya boyutu 5 MB'ı aşamaz.");
                return;
            }
            setFormError(null);
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setPhotoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const toLocalISOString = (date) => {
        if (!date) return null;
        const d = new Date(date);
        const tzOffset = d.getTimezoneOffset() * 60000;
        return (new Date(d.getTime() - tzOffset)).toISOString().slice(0, -1);
    };

    const submitForm = async (closeAfterSave = false) => {
        setFormError(null);
        setSubmitting(true);

        if (!formData.firstName.trim()) { setFormError('Ad boş bırakılamaz.'); setSubmitting(false); return; }
        if (!formData.lastName.trim()) { setFormError('Soyadı boş bırakılamaz.'); setSubmitting(false); return; }
        if (!formData.tcNo.trim() || formData.tcNo.length !== 11 || !/^\d{11}$/.test(formData.tcNo)) {
            setFormError('T.C. Kimlik Numarası 11 haneli ve sadece rakam olmalıdır.');
            setSubmitting(false); return;
        }
        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            setFormError('Geçerli bir e-posta adresi girin.');
            setSubmitting(false); return;
        }
        if (formData.phoneNumber && !/^0?5[0-9]{9}$/.test(formData.phoneNumber.replace(/\D/g, ''))) {
            setFormError('Geçerli bir cep telefonu girin (05XX XXX XX XX).');
            setSubmitting(false); return;
        }
        if (!formData.departmentId) { setFormError('Bölüm seçimi zorunludur.'); setSubmitting(false); return; }
        if (!formData.positionId) { setFormError('Pozisyon seçimi zorunludur.'); setSubmitting(false); return; }
        if (!formData.hireDate) { setFormError('İşe giriş tarihi zorunludur.'); setSubmitting(false); return; }

        if ((dataScope.dataAccessType === 2 || dataScope.dataAccessType === 5) && (!dataScope.departmentIds || dataScope.departmentIds.length === 0)) {
            setFormError('Bölüm bazlı veri görme kapsamında en az 1 bölüm seçmelisiniz.');
            setSubmitting(false); return;
        }
        if ((dataScope.dataAccessType === 4 || dataScope.dataAccessType === 5) && (!dataScope.locationIds || dataScope.locationIds.length === 0)) {
            setFormError('Lokasyon bazlı veri görme kapsamında en az 1 lokasyon seçmelisiniz.');
            setSubmitting(false); return;
        }

        if (formData.dateOfBirth && formData.hireDate) {
            const dob = new Date(formData.dateOfBirth);
            const hire = new Date(formData.hireDate);
            const minHire = new Date(dob);
            minHire.setFullYear(minHire.getFullYear() + 15);
            if (hire < minHire) {
                setFormError('İşe giriş tarihi, doğum tarihinden en az 15 yıl sonra olmalıdır.');
                setSubmitting(false); return;
            }
        }
        if (formData.hireDate && formData.terminationDate) {
            const hire = new Date(formData.hireDate);
            const term = new Date(formData.terminationDate);
            if (term < hire) {
                setFormError('İşten ayrılış tarihi, işe giriş tarihinden önce olamaz.');
                setSubmitting(false); return;
            }
        }

        try {
            const personnelData = {
                id: formData.id,
                firstName: formData.firstName,
                lastName: formData.lastName,
                turkishIdNo: formData.tcNo,
                sicilNo: formData.sicilNo || null,
                email: formData.email || null,
                phoneNumber: formData.phoneNumber ? String(formData.phoneNumber).replace(/\D/g, '') : null,
                departmentId: formData.departmentId ? Number(formData.departmentId) : null,
                positionId: formData.positionId ? Number(formData.positionId) : null,
                locationId: formData.locationId ? Number(formData.locationId) : null,
                workingRegimeId: formData.workingRegimeId ? Number(formData.workingRegimeId) : null,
                regimeEffectiveDate: syncWorkScheduleFromRegime && regimeEffectiveDate ? toLocalISOString(regimeEffectiveDate) : null,
                syncWorkScheduleFromRegime,
                hireDate: toLocalISOString(formData.hireDate),
                dateOfBirth: toLocalISOString(formData.dateOfBirth),
                terminationDate: toLocalISOString(formData.terminationDate),
                terminationReason: formData.terminationReason || null,
                seniorityDate: showAdvancedSeniority ? toLocalISOString(formData.seniorityDate) : null,
                previousLiquidatedYears: showAdvancedSeniority && formData.previousLiquidatedYears ? Number(formData.previousLiquidatedYears) : null,
                isActive: formData.isActive,
                userRole: formData.userRole,
                dataAccessType: dataScope.dataAccessType
            };

            const detailsData = {
                gender: formData.gender || null,
                locationId: formData.locationId ? Number(formData.locationId) : null,
                workingRegimeId: formData.workingRegimeId ? Number(formData.workingRegimeId) : null,
                regimeEffectiveDate: syncWorkScheduleFromRegime && regimeEffectiveDate ? toLocalISOString(regimeEffectiveDate) : null,
                syncWorkScheduleFromRegime,
                bloodType: formData.bloodType || null,
                cityId: formData.cityId ? Number(formData.cityId) : null,
                districtName: formData.districtName || null,
                neighborhood: formData.neighborhood || null,
                addressLine: formData.addressLine || null,
                postalCode: formData.postalCode || null,
                emergencyContactName: formData.emergencyContactName || null,
                emergencyContactPhone: formData.emergencyContactPhone || null,
                notes: formData.notes || null
            };

            await onSave({
                personnelData,
                scopeData: {
                    dataAccessType: dataScope.dataAccessType, // numeric 0..5
                    departmentIds: dataScope.departmentIds || [],
                    locationIds: dataScope.locationIds || []
                },
                detailsData,
                photoFile,
                subCompanyIds: canViewSubCompanies ? selectedSubCompanies : undefined,
                closeAfterSave
            });
        } catch (err) {
            setFormError(err.message || 'Kaydetme sırasında hata oluştu.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await submitForm(!isNew);
    };

    const title = isNew ? 'Yeni Personel Ekle' : 'Personel Düzenle';
    const submitButtonText = isNew ? 'Ekle' : 'Güncelle';

    return (
        <div className="w-full">
            {fetchingDropdowns ? (
                <div className="flex justify-center items-center py-20">
                    <div className="flex flex-col items-center space-y-4">
                        <Loader2 className="w-10 h-10 animate-spin text-indigo-500" />
                        <span className="text-gray-500 dark:text-gray-400 font-medium">Veriler Yükleniyor...</span>
                    </div>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    {formError && (
                        <div className="flex items-center p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl text-red-700 dark:text-red-300">
                            <AlertTriangle className="w-5 h-5 mr-2.5 flex-shrink-0" />
                            <p className="text-sm font-medium">{formError}</p>
                        </div>
                    )}
                    {(deptError || posError) && (
                        <div className="flex items-center p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl text-red-700 dark:text-red-300">
                            <AlertTriangle className="w-5 h-5 mr-2.5 flex-shrink-0" />
                            <p className="text-sm font-medium">{deptError || posError}</p>
                        </div>
                    )}

                    {/* GENİŞ 3 KOLONLU KOMPAKT DÜZEN */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                        {/* 1. KOLON: TEMEL KİMLİK, PROFİL FOTOĞRAFI VE İLETİŞİM */}
                        <div className="bg-gray-50/80 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200/80 dark:border-gray-700 space-y-3.5">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border-b border-gray-200 dark:border-gray-700 pb-2 flex items-center gap-1.5">
                                <User className="w-3.5 h-3.5" />
                                Kimlik &amp; İletişim Bilgileri
                            </h4>

                            {/* Kompakt Profil Fotoğrafı + Ad & Soyad */}
                            <div className="flex items-center gap-3.5">
                                <div className="flex flex-col items-center shrink-0">
                                    <div
                                        className="w-16 h-16 rounded-full overflow-hidden bg-white dark:bg-gray-700 border-2 border-indigo-100 dark:border-gray-600 shadow-sm flex items-center justify-center cursor-pointer relative group"
                                        onClick={() => fileInputRef.current?.click()}
                                        title="Profil fotoğrafını seç veya değiştir"
                                    >
                                        {photoPreview ? (
                                            <img
                                                src={photoPreview}
                                                alt="Preview"
                                                onError={(e) => { e.target.style.display = 'none'; setPhotoPreview(null); }}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <User className="w-8 h-8 text-gray-300 dark:text-gray-500" />
                                        )}
                                        <div className="absolute inset-0 bg-black/45 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Upload className="w-4 h-4 text-white" />
                                        </div>
                                    </div>
                                    <input type="file" ref={fileInputRef} onChange={handlePhotoChange} accept="image/jpeg, image/png, image/webp" className="hidden" />
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="mt-1 text-[11px] text-indigo-600 dark:text-indigo-400 font-semibold hover:underline"
                                    >
                                        {photoPreview ? 'Değiştir' : 'Fotoğraf'}
                                    </button>
                                </div>

                                <div className="flex-1 grid grid-cols-2 gap-2.5">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Ad *</label>
                                        <input
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Soyad *</label>
                                        <input
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* T.C. Kimlik No & Sicil No */}
                            <div className="grid grid-cols-2 gap-2.5">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">T.C. Kimlik No *</label>
                                    <input
                                        name="tcNo"
                                        value={formData.tcNo}
                                        onChange={handleChange}
                                        required
                                        maxLength="11"
                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all font-mono"
                                    />
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Sicil No</label>
                                        {!isNew && personnel?.id && (
                                            <button
                                                type="button"
                                                onClick={() => setIsSicilUnlocked(prev => !prev)}
                                                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded transition-colors ${
                                                    isSicilUnlocked
                                                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                                                        : 'bg-gray-200/80 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                                                }`}
                                                title={isSicilUnlocked ? 'Sicil No alanını kilitle' : 'Sicil No değiştirmek için kilidi aç'}
                                            >
                                                {isSicilUnlocked ? <Unlock size={10} /> : <Lock size={10} />}
                                                {isSicilUnlocked ? 'Açık' : 'Kilitli'}
                                            </button>
                                        )}
                                    </div>
                                    <input
                                        name="sicilNo"
                                        value={formData.sicilNo}
                                        onChange={handleChange}
                                        readOnly={!isNew && !!personnel?.id && !isSicilUnlocked}
                                        placeholder="Otomatik Üretilir"
                                        className={`w-full px-2.5 py-1.5 text-sm border rounded-lg font-mono transition-all ${
                                            !isNew && !!personnel?.id && !isSicilUnlocked
                                                ? 'bg-gray-100 dark:bg-gray-900/60 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                                : 'border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white'
                                        }`}
                                    />
                                </div>
                            </div>

                            {!isNew && isSicilUnlocked && (
                                <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 text-[11px] text-amber-800 dark:text-amber-300 flex items-start gap-1.5">
                                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                                    <span>
                                        Firma giriş tercihi <em>Sicil No</em> ise personelin giriş kullanıcı adı da yeni Sicil Numarasına güncellenir.
                                    </span>
                                </div>
                            )}

                            {/* Doğum Tarihi, Cinsiyet ve Kan Grubu (3'lü Kompakt Satır) */}
                            <div className="grid grid-cols-12 gap-2.5">
                                <div className="col-span-5">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Doğum Tarihi *</label>
                                    <CustomDatePicker selectedDate={formData.dateOfBirth} onDateChange={(d) => handleDateChange(d, 'dateOfBirth')} placeholder="GG.AA.YYYY" />
                                </div>
                                <div className="col-span-4">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Cinsiyet</label>
                                    <select
                                        name="gender"
                                        value={formData.gender || 'Male'}
                                        onChange={handleChange}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
                                    >
                                        <option value="Male">Erkek</option>
                                        <option value="Female">Kadın</option>
                                    </select>
                                </div>
                                <div className="col-span-3">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Kan Grb.</label>
                                    <select
                                        name="bloodType"
                                        value={formData.bloodType}
                                        onChange={handleChange}
                                        className="w-full px-1.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
                                    >
                                        <option value="">Seç</option>
                                        <option value="A Rh+">A Rh+</option>
                                        <option value="A Rh-">A Rh-</option>
                                        <option value="B Rh+">B Rh+</option>
                                        <option value="B Rh-">B Rh-</option>
                                        <option value="AB Rh+">AB Rh+</option>
                                        <option value="AB Rh-">AB Rh-</option>
                                        <option value="0 Rh+">0 Rh+</option>
                                        <option value="0 Rh-">0 Rh-</option>
                                    </select>
                                </div>
                            </div>

                            {/* İletişim (Telefon & E-Posta Yan Yana) */}
                            <div className="pt-2.5 border-t border-gray-200/80 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Telefon Numarası</label>
                                    <input
                                        type="tel"
                                        name="phoneNumber"
                                        value={formatPhoneNumberDisplay(formData.phoneNumber)}
                                        onChange={handleChange}
                                        placeholder="0 (5XX) XXX XX XX"
                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">E-Posta</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="ornek@sirket.com"
                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all"
                                    />
                                </div>
                            </div>

                            {/* Acil Durum Kişisi & Telefonu */}
                            <div className="pt-2.5 border-t border-gray-200/80 dark:border-gray-700 grid grid-cols-2 gap-2.5">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Acil Durum Kişisi</label>
                                    <input
                                        type="text"
                                        name="emergencyContactName"
                                        value={formData.emergencyContactName}
                                        onChange={handleChange}
                                        placeholder="Ad Soyad"
                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Acil Durum Tel.</label>
                                    <input
                                        type="text"
                                        name="emergencyContactPhone"
                                        value={formData.emergencyContactPhone}
                                        onChange={handleChange}
                                        placeholder="05XX XXX XX XX"
                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 2. KOLON: KURUMSAL & SİSTEM BİLGİLERİ */}
                        <div className="bg-gray-50/80 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200/80 dark:border-gray-700 space-y-3.5">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border-b border-gray-200 dark:border-gray-700 pb-2 flex items-center gap-1.5">
                                <Briefcase className="w-3.5 h-3.5" />
                                Kurumsal &amp; Sistem Bilgileri
                            </h4>

                            <div className="grid grid-cols-2 gap-2.5">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Bölüm *</label>
                                    <select
                                        name="departmentId"
                                        value={formData.departmentId}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all"
                                    >
                                        <option value="">Seçiniz</option>
                                        {departments
                                            .filter(dep => dep.isActive !== false || String(dep.id) === String(formData.departmentId))
                                            .map(dep => (
                                                <option key={dep.id} value={dep.id}>
                                                    {dep.name}{dep.isActive === false ? ' (Pasif)' : ''}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Pozisyon *</label>
                                    <select
                                        name="positionId"
                                        value={formData.positionId}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all"
                                    >
                                        <option value="">Seçiniz</option>
                                        {positions
                                            .filter(pos => pos.isActive !== false || String(pos.id) === String(formData.positionId))
                                            .map(pos => (
                                                <option key={pos.id} value={pos.id}>
                                                    {pos.name}{pos.isActive === false ? ' (Pasif)' : ''}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2.5">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Lokasyon</label>
                                    <select
                                        name="locationId"
                                        value={formData.locationId}
                                        onChange={handleChange}
                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all"
                                    >
                                        <option value="">Seçiniz</option>
                                        {locations
                                            .filter(loc => loc.isActive !== false || String(loc.id) === String(formData.locationId))
                                            .map(loc => (
                                                <option key={loc.id} value={loc.id}>
                                                    {loc.name}{loc.isActive === false ? ' (Pasif)' : ''}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Çalışma Rejimi</label>
                                    <select
                                        name="workingRegimeId"
                                        value={formData.workingRegimeId}
                                        onChange={handleChange}
                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all"
                                    >
                                        <option value="">Seçiniz</option>
                                        {workingRegimes
                                            .filter(wr => wr.isActive !== false || String(wr.id) === String(formData.workingRegimeId))
                                            .map(wr => (
                                                <option key={wr.id} value={wr.id}>
                                                    {wr.name}{wr.defaultWorkPeriodName ? ` (${wr.defaultWorkPeriodName})` : ''}{wr.isActive === false ? ' (Pasif)' : ''}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                            </div>

                            {/* Şu Anki Aktif Vardiya & Çalışma Rejimi Vardiya Senkronizasyon Kutusu */}
                            {(() => {
                                const selectedRegimeObj = workingRegimes.find(w => String(w.id) === String(formData.workingRegimeId));
                                const regimeChanged = !isNew && String(formData.workingRegimeId || '') !== String(personnel?.workingRegimeId || '');
                                const hasTargetPeriod = !!selectedRegimeObj?.defaultWorkPeriodId;
                                const isDifferentFromTarget = hasTargetPeriod && (
                                    !personnel?.currentWorkPeriodId ||
                                    Number(personnel.currentWorkPeriodId) !== Number(selectedRegimeObj.defaultWorkPeriodId)
                                );

                                return (
                                    <div className="p-2.5 rounded-lg bg-white/90 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 space-y-2 text-xs">
                                        {!isNew && (
                                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                                <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1 font-medium">
                                                    <Clock className="w-3.5 h-3.5 text-indigo-500" />
                                                    Şu Anki Vardiya:
                                                </span>
                                                {personnel?.currentWorkPeriodName ? (
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <span className={`px-2 py-0.5 rounded font-semibold ${
                                                            personnel.isCurrentScheduleTemporary || personnel.isCurrentScheduleDifferentFromRegime
                                                                ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                                                                : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60'
                                                        }`}>
                                                            {personnel.currentWorkPeriodName}
                                                        </span>
                                                        {personnel.isCurrentScheduleTemporary && personnel.currentScheduleEndDate && (
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 font-medium">
                                                                Geçici ({new Date(personnel.currentScheduleEndDate).toLocaleDateString('tr-TR')})
                                                            </span>
                                                        )}
                                                        {!personnel.isCurrentScheduleTemporary && personnel.isCurrentScheduleDifferentFromRegime && (
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 font-medium" title={`Rejim Varsayılanı: ${personnel.regimeDefaultWorkPeriodName || '-'}`}>
                                                                Rejimden Farklı
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="px-2 py-0.5 rounded bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300 font-medium">
                                                        Vardiya Atanmamış
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {hasTargetPeriod && (isNew || regimeChanged || isDifferentFromTarget) && (
                                            <div className={`${!isNew ? 'pt-2 border-t border-gray-200/80 dark:border-gray-700/80' : ''} space-y-2`}>
                                                <label className="flex items-start gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={syncWorkScheduleFromRegime}
                                                        onChange={(e) => setSyncWorkScheduleFromRegime(e.target.checked)}
                                                        className="mt-0.5 h-3.5 w-3.5 text-indigo-600 rounded border-gray-300"
                                                    />
                                                    <span className="text-[11px] text-gray-700 dark:text-gray-200 leading-tight">
                                                        <strong>{selectedRegimeObj.defaultWorkPeriodName}</strong> vardiyasını otomatik tanımla
                                                    </span>
                                                </label>

                                                {syncWorkScheduleFromRegime && !isNew && (
                                                    <div className="flex items-center justify-between gap-2 pl-5">
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-[11px] text-gray-500 dark:text-gray-400">Geçerlilik:</span>
                                                            <input
                                                                type="date"
                                                                value={regimeEffectiveDate}
                                                                onChange={(e) => setRegimeEffectiveDate(e.target.value)}
                                                                className="px-2 py-1 text-[11px] border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                                            />
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                type="button"
                                                                onClick={() => setRegimeEffectiveDate(formatDateForInput(new Date()))}
                                                                className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-100"
                                                            >
                                                                Bugün
                                                            </button>
                                                            {formData.hireDate && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setRegimeEffectiveDate(formData.hireDate)}
                                                                    className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                                                                >
                                                                    İşe Giriş
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            <div className="grid grid-cols-2 gap-2.5">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex justify-between items-center">
                                        <span>İşe Giriş *</span>
                                        {personnel?.id && (
                                            <button
                                                type="button"
                                                onClick={() => setIsHistoryModalOpen(true)}
                                                className="text-[11px] text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 flex items-center font-semibold"
                                                title="Personelin geçmiş işe giriş/çıkış dönemlerini görüntüle"
                                            >
                                                <Calendar size={11} className="mr-0.5" /> Geçmiş
                                            </button>
                                        )}
                                    </label>
                                    <CustomDatePicker selectedDate={formData.hireDate} onDateChange={(d) => handleDateChange(d, 'hireDate')} placeholder="GG.AA.YYYY" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">İşten Çıkış</label>
                                    <CustomDatePicker selectedDate={formData.terminationDate} onDateChange={(d) => handleDateChange(d, 'terminationDate')} placeholder="GG.AA.YYYY" />
                                </div>
                            </div>

                            {formData.terminationDate && (
                                <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Çıkış Nedeni (Opsiyonel)</label>
                                    <input
                                        type="text"
                                        name="terminationReason"
                                        value={formData.terminationReason}
                                        onChange={handleChange}
                                        placeholder="Örn: İstifa, Sözleşme Bitimi"
                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all"
                                    />
                                </div>
                            )}

                            {/* Kullanıcı Rolü & Sistem Aktifliği Yan Yana */}
                            <div className="grid grid-cols-12 gap-2.5 pt-2 border-t border-gray-200/80 dark:border-gray-700 items-end">
                                <div className="col-span-6">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Kullanıcı Rolü *</label>
                                    <select
                                        name="userRole"
                                        value={formData.userRole}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all"
                                    >
                                        {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                                        {roles.length === 0 && <option value="Personel">Personel</option>}
                                    </select>
                                </div>
                                <div className="col-span-6">
                                    <label
                                        htmlFor="isActive"
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer select-none transition-colors ${
                                            formData.isActive
                                                ? 'bg-emerald-50/70 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/60 text-emerald-800 dark:text-emerald-300'
                                                : 'bg-red-50/70 dark:bg-red-900/20 border-red-200 dark:border-red-800/60 text-red-800 dark:text-red-300'
                                        }`}
                                    >
                                        <input
                                            id="isActive"
                                            name="isActive"
                                            type="checkbox"
                                            checked={formData.isActive}
                                            onChange={handleChange}
                                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                        />
                                        <span className="text-xs font-semibold truncate">
                                            {formData.isActive ? 'Sistemde Aktif' : 'Pasif (Çıkışlı)'}
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {/* Özel Kıdem Ayarları */}
                            <div className="pt-2 border-t border-gray-200/80 dark:border-gray-700">
                                <div className="flex items-center">
                                    <input
                                        id="showAdvancedSeniority"
                                        type="checkbox"
                                        checked={showAdvancedSeniority}
                                        onChange={(e) => setShowAdvancedSeniority(e.target.checked)}
                                        className="h-3.5 w-3.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 bg-white dark:bg-gray-700"
                                    />
                                    <label htmlFor="showAdvancedSeniority" className="ml-2 block text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                                        Özel Kıdem Ayarları (Geçmiş Kıdem / Devir)
                                    </label>
                                </div>

                                {showAdvancedSeniority && (
                                    <div className="mt-2.5 p-2.5 bg-indigo-50/70 dark:bg-indigo-900/15 rounded-lg border border-indigo-100 dark:border-indigo-800/40 grid grid-cols-2 gap-2.5 animate-in fade-in duration-200">
                                        <div>
                                            <label className="block text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Kıdem Başlangıç
                                            </label>
                                            <CustomDatePicker selectedDate={formData.seniorityDate} onDateChange={(d) => handleDateChange(d, 'seniorityDate')} placeholder="GG.AA.YYYY" />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Geçmiş Kıdem (Yıl)
                                            </label>
                                            <input
                                                type="number"
                                                name="previousLiquidatedYears"
                                                value={formData.previousLiquidatedYears}
                                                onChange={handleChange}
                                                placeholder="Örn: 3"
                                                min="0"
                                                max="50"
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Holding Alt Firma Erişimi */}
                            {canViewSubCompanies && (
                                <div className="pt-2.5 border-t border-gray-200/80 dark:border-gray-700">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-1.5">
                                            <GitBranch className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">Alt Firma Erişimi (Holding)</span>
                                        </div>
                                        {subCompanies.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    selectedSubCompanies.length === subCompanies.length
                                                        ? setSelectedSubCompanies([])
                                                        : setSelectedSubCompanies(subCompanies.map(s => s.id))
                                                }
                                                className="text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                                            >
                                                {selectedSubCompanies.length === subCompanies.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                                            </button>
                                        )}
                                    </div>
                                    {loadingSubCompanies ? (
                                        <div className="flex items-center justify-center py-3 text-gray-400 text-xs">
                                            <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Yükleniyor...
                                        </div>
                                    ) : subCompanies.length === 0 ? (
                                        <p className="text-xs text-gray-400 italic">Bağlı alt firma bulunamadı.</p>
                                    ) : (
                                        <div className="space-y-1.5 max-h-32 overflow-y-auto pr-1 custom-scrollbar">
                                            {subCompanies.map(sub => {
                                                const isChecked = selectedSubCompanies.includes(sub.id);
                                                return (
                                                    <button
                                                        type="button"
                                                        key={sub.id}
                                                        onClick={() => handleToggleSubCompany(sub.id)}
                                                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-left text-xs transition-all ${
                                                            isChecked
                                                                ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-400 dark:border-indigo-600'
                                                                : 'bg-white dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                                                        }`}
                                                    >
                                                        <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                                                            isChecked ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 dark:border-gray-500'
                                                        }`}>
                                                            {isChecked && <Check className="w-2.5 h-2.5 text-white" />}
                                                        </div>
                                                        <span className="font-medium truncate flex-1">{sub.firmName}</span>
                                                        <span className="font-mono text-[10px] text-gray-400">{sub.firmCode.toUpperCase()}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* 3. KOLON: VERİ GÖRME KAPSAMI & ADRES / EK DETAYLAR */}
                        <div className="space-y-4">
                            {/* Veri Görme Kapsamı */}
                            <div className="bg-gray-50/80 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200/80 dark:border-gray-700 space-y-3">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border-b border-gray-200 dark:border-gray-700 pb-2 flex items-center gap-1.5">
                                    <CheckSquare className="w-3.5 h-3.5" />
                                    Veri Görme Kapsamı
                                </h4>
                                <select
                                    name="dataAccessType"
                                    value={dataScope.dataAccessType}
                                    onChange={handleDataScopeTypeChange}
                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all"
                                >
                                    <option value={1}>Sadece Kendi Verileri</option>
                                    <option value={2}>Sadece Bölüm Bazlı Görebilir</option>
                                    <option value={4}>Sadece Lokasyon Bazlı Görebilir</option>
                                    <option value={5}>Bölüm + Lokasyon Bazlı Görebilir (Birlikte)</option>
                                    <option value={3}>Tüm Verileri Görebilir</option>
                                </select>

                                {dataScope.dataAccessType === 5 && (
                                    <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800/50 text-[11px] text-indigo-800 dark:text-indigo-200">
                                        Seçilen <strong>lokasyon(lar)daki</strong> ve aynı zamanda seçilen <strong>bölüm(ler)de</strong> yer alan personeller görüntülenebilir.
                                    </div>
                                )}

                                {(dataScope.dataAccessType === 2 || dataScope.dataAccessType === 4 || dataScope.dataAccessType === 5) && (
                                    <div className={`grid gap-2.5 ${dataScope.dataAccessType === 5 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                                        {(dataScope.dataAccessType === 2 || dataScope.dataAccessType === 5) && (
                                            <div className="p-2.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <label className="block text-[11px] font-semibold text-gray-600 dark:text-gray-400 uppercase">
                                                        Bölümler ({dataScope.departmentIds.length})
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const activeIds = departments.filter(d => d.isActive !== false).map(d => d.id);
                                                            const allSelected = activeIds.length > 0 && activeIds.every(id => dataScope.departmentIds.includes(id));
                                                            setDataScope(prev => ({ ...prev, departmentIds: allSelected ? [] : activeIds }));
                                                        }}
                                                        className="text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                                                    >
                                                        {departments.filter(d => d.isActive !== false).length > 0 && departments.filter(d => d.isActive !== false).every(d => dataScope.departmentIds.includes(d.id))
                                                            ? 'Temizle'
                                                            : 'Tümü'}
                                                    </button>
                                                </div>
                                                <div className="max-h-28 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                                                    {departments.map(dep => (
                                                        <label key={dep.id} className="flex items-center justify-between p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer transition-colors">
                                                            <div className="flex items-center min-w-0">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={dataScope.departmentIds.includes(dep.id)}
                                                                    onChange={() => handleDepartmentCheckboxChange(dep.id)}
                                                                    className="h-3.5 w-3.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 shrink-0"
                                                                />
                                                                <span className="ml-2 text-xs text-gray-800 dark:text-gray-200 truncate">
                                                                    {dep.name} {dep.isActive === false ? '(Pasif)' : ''}
                                                                </span>
                                                            </div>
                                                        </label>
                                                    ))}
                                                    {departments.length === 0 && (
                                                        <div className="text-xs text-gray-400 italic py-1">Bölüm yok.</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {(dataScope.dataAccessType === 4 || dataScope.dataAccessType === 5) && (
                                            <div className="p-2.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <label className="block text-[11px] font-semibold text-gray-600 dark:text-gray-400 uppercase">
                                                        Lokasyonlar ({dataScope.locationIds.length})
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const activeIds = locations.filter(l => l.isActive !== false).map(l => l.id);
                                                            const allSelected = activeIds.length > 0 && activeIds.every(id => dataScope.locationIds.includes(id));
                                                            setDataScope(prev => ({ ...prev, locationIds: allSelected ? [] : activeIds }));
                                                        }}
                                                        className="text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                                                    >
                                                        {locations.filter(l => l.isActive !== false).length > 0 && locations.filter(l => l.isActive !== false).every(l => dataScope.locationIds.includes(l.id))
                                                            ? 'Temizle'
                                                            : 'Tümü'}
                                                    </button>
                                                </div>
                                                <div className="max-h-28 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                                                    {locations.map(loc => (
                                                        <label key={loc.id} className="flex items-center justify-between p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer transition-colors">
                                                            <div className="flex items-center min-w-0">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={dataScope.locationIds.includes(loc.id)}
                                                                    onChange={() => handleLocationCheckboxChange(loc.id)}
                                                                    className="h-3.5 w-3.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 shrink-0"
                                                                />
                                                                <span className="ml-2 text-xs text-gray-800 dark:text-gray-200 truncate">
                                                                    {loc.name} {loc.isActive === false ? '(Pasif)' : ''}
                                                                </span>
                                                            </div>
                                                        </label>
                                                    ))}
                                                    {locations.length === 0 && (
                                                        <div className="text-xs text-gray-400 italic py-1">Lokasyon yok.</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Adres ve Notlar (Kompakt Kart) */}
                            <div className="bg-gray-50/80 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200/80 dark:border-gray-700 space-y-2.5">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border-b border-gray-200 dark:border-gray-700 pb-2 flex items-center gap-1.5">
                                    <Info className="w-3.5 h-3.5" />
                                    Adres &amp; İK Notları
                                </h4>

                                <div className="grid grid-cols-12 gap-2">
                                    <div className="col-span-5">
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">İl</label>
                                        <select
                                            name="cityId"
                                            value={formData.cityId}
                                            onChange={(e) => { handleChange(e); setFormData(p => ({ ...p, districtName: '' })); }}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
                                        >
                                            <option value="">Seçiniz</option>
                                            {turkeyCities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-span-4">
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">İlçe</label>
                                        <select
                                            name="districtName"
                                            value={formData.districtName}
                                            onChange={handleChange}
                                            disabled={!formData.cityId}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                                        >
                                            <option value="">Seçiniz</option>
                                            {(turkeyCities.find(c => c.id === Number(formData.cityId))?.districts || []).map(d => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-span-3">
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">P. Kodu</label>
                                        <input
                                            type="text"
                                            name="postalCode"
                                            value={formData.postalCode}
                                            onChange={handleChange}
                                            placeholder="34000"
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-12 gap-2">
                                    <div className="col-span-5">
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Mahalle</label>
                                        <input
                                            type="text"
                                            name="neighborhood"
                                            value={formData.neighborhood}
                                            onChange={handleChange}
                                            placeholder="Mahalle"
                                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div className="col-span-7">
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Açık Adres</label>
                                        <input
                                            type="text"
                                            name="addressLine"
                                            value={formData.addressLine}
                                            onChange={handleChange}
                                            placeholder="Sokak, Bina, Kapı No..."
                                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Personel Notu</label>
                                    <textarea
                                        rows={2}
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        placeholder="Personel hakkında ek notlar..."
                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white resize-none"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="flex flex-wrap justify-end gap-2.5 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <button type="button" onClick={onCancel} disabled={currentSubmittingState}
                            className="px-5 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors">
                            İptal
                        </button>
                        {!isNew && (
                            <button
                                type="button"
                                onClick={() => submitForm(false)}
                                disabled={currentSubmittingState || fetchingDropdowns}
                                className="px-4 py-2 text-sm bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 rounded-lg font-medium transition-colors flex items-center disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                <CreditCard className="w-4 h-4 mr-1.5" />
                                Güncelle &amp; Cihaz Adımına Geç
                            </button>
                        )}
                        <button type="submit" disabled={currentSubmittingState || fetchingDropdowns}
                            className="px-5 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors flex items-center disabled:opacity-70 disabled:cursor-not-allowed shadow-sm">
                            {currentSubmittingState ? (
                                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Kaydediliyor...</>
                            ) : (
                                <><Save className="w-4 h-4 mr-1.5" /> {!isNew ? 'Güncelle ve Kapat' : submitButtonText}</>
                            )}
                        </button>
                    </div>
                </form>
            )}
            {isHistoryModalOpen && (
                <EmploymentHistoryModal
                    isOpen={isHistoryModalOpen}
                    onClose={() => setIsHistoryModalOpen(false)}
                    employeeId={personnel?.id}
                    employeeName={`${formData.firstName} ${formData.lastName}`}
                />
            )}
        </div>
    );
}

